import {
  BarApplicationConfiguration,
  BrandApplicationConfiguration,
  FrontDeskApplicationConfiguration,
  HousekeepingApplicationConfiguration,
  KioskApplicationConfiguration,
  LocationApplicationConfiguration,
  RoomApplicationConfiguration,
  StaffApplicationConfiguration,
} from '@era/settings-sdk';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { CategoryType } from '../shared/types';

export interface ApplicationConfiguration {
  type: CategoryType;
  id: string;
  locationId?: string;
  displayName: string;
  bundleIdentifier: string;
  version: string;
  applyFrom?: Date;
  applyTill?: Date;
}

type AnyApplicationConfiguration =
  | BrandApplicationConfiguration
  | LocationApplicationConfiguration
  | RoomApplicationConfiguration
  | StaffApplicationConfiguration
  | KioskApplicationConfiguration
  | HousekeepingApplicationConfiguration
  | BarApplicationConfiguration
  | FrontDeskApplicationConfiguration;

function getLocationId(configuration: AnyApplicationConfiguration): string | undefined {
  if ('locationId' in configuration) {
    const value = configuration.locationId;
    return typeof value === 'string' ? value : undefined;
  }
  return undefined;
}

/**
 * Generic function to convert any application configuration to the unified format.
 * Automatically determines the category type and ID based on the input configuration.
 */
export function applicationConfigurationFrom(configuration: AnyApplicationConfiguration): ApplicationConfiguration {
  // Type guard functions to determine the category type
  if ('brandId' in configuration) {
    return {
      type: 'Brand',
      id: configuration.brandId,
      locationId: getLocationId(configuration),
      displayName: configuration.displayName,
      bundleIdentifier: configuration.bundleIdentifier,
      version: configuration.version,
      applyFrom: configuration.applyFrom,
      applyTill: configuration.applyTill,
    };
  }
  if ('locationId' in configuration) {
    return {
      type: 'Location',
      id: configuration.locationId,
      locationId: configuration.locationId,
      displayName: configuration.displayName,
      bundleIdentifier: configuration.bundleIdentifier,
      version: configuration.version,
      applyFrom: configuration.applyFrom,
      applyTill: configuration.applyTill,
    };
  }
  if ('roomId' in configuration) {
    return {
      type: 'Room',
      id: configuration.roomId,
      locationId: getLocationId(configuration),
      displayName: configuration.displayName,
      bundleIdentifier: configuration.bundleIdentifier,
      version: configuration.version,
      applyFrom: configuration.applyFrom,
      applyTill: configuration.applyTill,
    };
  }
  if ('barId' in configuration) {
    return {
      type: 'Bar',
      id: configuration.barId,
      locationId: getLocationId(configuration),
      displayName: configuration.displayName,
      bundleIdentifier: configuration.bundleIdentifier,
      version: configuration.version,
      applyFrom: configuration.applyFrom,
      applyTill: configuration.applyTill,
    };
  }
  if ('frontDeskId' in configuration) {
    return {
      type: 'FrontDesk',
      id: configuration.frontDeskId,
      locationId: getLocationId(configuration),
      displayName: configuration.displayName,
      bundleIdentifier: configuration.bundleIdentifier,
      version: configuration.version,
      applyFrom: configuration.applyFrom,
      applyTill: configuration.applyTill,
    };
  }
  if ('housekeepingId' in configuration) {
    return {
      type: 'Housekeeping',
      id: configuration.housekeepingId,
      locationId: getLocationId(configuration),
      displayName: configuration.displayName,
      bundleIdentifier: configuration.bundleIdentifier,
      version: configuration.version,
      applyFrom: configuration.applyFrom,
      applyTill: configuration.applyTill,
    };
  }
  if ('kioskId' in configuration) {
    return {
      type: 'Kiosk',
      id: configuration.kioskId,
      locationId: getLocationId(configuration),
      displayName: configuration.displayName,
      bundleIdentifier: configuration.bundleIdentifier,
      version: configuration.version,
      applyFrom: configuration.applyFrom,
      applyTill: configuration.applyTill,
    };
  }
  if ('staffId' in configuration) {
    return {
      type: 'Staff',
      id: configuration.staffId,
      locationId: getLocationId(configuration),
      displayName: configuration.displayName,
      bundleIdentifier: configuration.bundleIdentifier,
      version: configuration.version,
      applyFrom: configuration.applyFrom,
      applyTill: configuration.applyTill,
    };
  }
  throw new Error('Unknown application configuration type');
}

export type CategoryApplicationsState = EntityState<ApplicationConfiguration>;

export function selectId(application: ApplicationConfiguration) {
  return `${application.type}-${application.id}-${application.bundleIdentifier}-${application.version}`;
}

export const adapter: EntityAdapter<ApplicationConfiguration> = createEntityAdapter<ApplicationConfiguration>({
  selectId,
});

export const initialState: CategoryApplicationsState = adapter.getInitialState();
